import { capitalize, uniq } from 'lodash';

import { type DenType, RenewalStatus } from '@appTypes/enums';
import type { OrgSubUnit, Rank, SubUnitType } from '@appTypes/esb';
import { getRegistrationStatus } from '@modules/advancement/packRoster/utilsTyped';
import {
  denTypes,
  denTypesRankIds,
  denTypesSingleNames,
} from '@shared/constants';
import { getPreferredName as getPreferredNameUtil } from '@shared/utils/preferredName';

import type { RosterMember, SubUnitAssignment } from './rosterMember';
import { MemberKind, getAge, getMaxYouthAge } from './rosterMember';

/**
 * Normalize API's renewal status by stripping the " (unit only)" suffix
 * (e.g. "Current (unit only)" -> "Current"), so the value matches the
 * `RenewalStatus` enum for display, filtering, and sorting
 */
function normalizeRenewalStatus(
  raw?: RenewalStatus,
): RenewalStatus | undefined {
  if (!raw) return undefined;
  const [clean] = String(raw).split(' (unit only)');
  const normalized = clean.trim();
  return (Object.values(RenewalStatus) as string[]).includes(normalized)
    ? (normalized as RenewalStatus)
    : undefined;
}

function getFullName(m: RosterMember): string {
  return `${m.firstName} ${m.lastName}`.trim();
}

function getAgeDisplay(m: RosterMember): string {
  const age = getAge(m);
  if (m.kind === MemberKind.Adult) {
    return (age ?? 0) > 21 ? '(21+)' : '(18-21)';
  }
  return age === null ? '—' : String(age);
}

/** `<Level> Den - <name>` when there is a den level, else the trimmed name */
export function subUnitLabel(
  denType: string | null | undefined,
  name: string | null | undefined,
): string | undefined {
  const level = denType?.trim();
  const trimmedName = name?.trim();
  if (level) {
    const singleName = denTypesSingleNames[level.toLowerCase()];
    const levelName =
      singleName === denTypesSingleNames.aol
        ? 'AOL'
        : capitalize(singleName ?? level);
    const denName = `${levelName} Den`;
    return trimmedName ? `${denName} - ${trimmedName}` : denName;
  }

  return trimmedName || undefined;
}

/** Same wording as the Sub-Unit column; a patrol's `denType` is ignored. */
export function formatSubUnitLabel(
  subUnit: Pick<OrgSubUnit, 'subUnitName'> & { denType?: string | null },
  subUnitType: SubUnitType,
): string {
  const denType = subUnitType === 'den' ? subUnit.denType : undefined;
  return subUnitLabel(denType, subUnit.subUnitName) ?? '';
}

function denLevelLabel(denType: string): string {
  const label = denTypes.find(({ name }) => name === denType)?.label;
  return label ?? capitalize(denTypesSingleNames[denType] ?? denType);
}

/** `value` is a `denTypesNames` value */
export const DEN_LEVEL_OPTIONS = denTypes.map(({ name }) => ({
  value: name,
  label: denLevelLabel(name),
}));

/** Den level image urls from the ranks; a level without one shows as text. */
export function buildDenLevelImages(
  ranks: readonly Pick<Rank, 'id' | 'versions'>[] | undefined,
): Partial<Record<string, string>> {
  if (!ranks?.length) return {};
  const imageByRankId = new Map(
    ranks.map(rank => {
      const version = rank.versions?.find(v => v.active) ?? rank.versions?.[0];
      // String keys: the lookup's ids may come back as strings
      return [String(rank.id), version?.imageUrl100] as const;
    }),
  );
  return denTypes.reduce<Partial<Record<string, string>>>(
    (images, { name }) => {
      // `denTypes` names are `DenType` values
      const url = imageByRankId.get(String(denTypesRankIds[name as DenType]));
      return url ? { ...images, [name]: url } : images;
    },
    {},
  );
}

export interface SubUnitRef {
  id: number;
  type: SubUnitType;
  /** Same wording as `subUnitNames` */
  label: string;
  /** Dens only: the trimmed API den level, e.g. `wolves` */
  denType?: string;
}

/** Also the group key and Sub-Unit filter value: `den:12` */
export function subUnitKey(ref: Pick<SubUnitRef, 'type' | 'id'>): string {
  return `${ref.type}:${ref.id}`;
}

/**
 * A den when it has a `denId`, even with no level. With both ids, a den only
 * with a den level: patrol entries may carry a placeholder `denId`. 0 is no id.
 */
export function assignmentRef(
  assignment: SubUnitAssignment,
): Pick<SubUnitRef, 'type' | 'id'> | undefined {
  const denId = assignment.denId || undefined;
  const patrolId = assignment.patrolId || undefined;
  const hasDenLevel = !!assignment.denType?.trim();
  if (denId !== undefined && (patrolId === undefined || hasDenLevel))
    return { type: 'den', id: denId };
  if (patrolId !== undefined) return { type: 'patrol', id: patrolId };
  return undefined;
}

function getSubUnitRefs(assignments: SubUnitAssignment[]): SubUnitRef[] {
  const byKey = new Map<string, SubUnitRef>();
  assignments.forEach(assignment => {
    const ref = assignmentRef(assignment);
    if (!ref) return;
    const denType =
      ref.type === 'den' ? assignment.denType?.trim() || undefined : undefined;
    const label =
      ref.type === 'den'
        ? subUnitLabel(denType, assignment.denNumber)
        : subUnitLabel(undefined, assignment.patrolName);
    if (!label) return;
    const key = subUnitKey(ref);
    if (!byKey.has(key))
      byKey.set(key, denType ? { ...ref, label, denType } : { ...ref, label });
  });
  return Array.from(byKey.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * Builds a UI view-model from a plain {@link RosterMember} with computed
 * accessors (preferredName, displayAge, etc.) for the table layer
 */
export function createMemberView(member: RosterMember) {
  const age = getAge(member);
  const subUnits = getSubUnitRefs(member.subUnit?.assignments ?? []);
  // Sorted, as the refs are
  const subUnitNames = uniq(subUnits.map(ref => ref.label));
  return {
    ...member,
    /** Renewal lifecycle status (Current, Eligible to Renew, ...). */
    renewalStatus: normalizeRenewalStatus(member.registration?.renewalStatus),
    /** Registration situation status (Active / Lapsed / Expired). */
    registrationStatus: member.registration
      ? getRegistrationStatus(member.registration)
      : undefined,
    preferredName: getPreferredNameUtil(member),
    fullName: getFullName(member),
    age,
    displayAge: getAgeDisplay(member),
    isAdult: member.kind === MemberKind.Adult,
    isYouth: member.kind === MemberKind.Youth,
    isParticipant: member.kind === MemberKind.Participant,
    maxYouthAge: getMaxYouthAge(member),
    isOverYouthAge: member.kind === MemberKind.Participant,
    /** Highest rank achieved, populated once the advancement enrichment slice loads. */
    lastRankApproved: member.advancement?.currentHighestRank,
    /** Display-ready sub-unit names populated by sub-unit enrichment. */
    subUnitNames,
    displaySubUnits: subUnitNames.join(', '),
    /** With their ids, for the Sub-Unit cell actions */
    subUnits,
    /** Passthrough from registrarInfo */
    registrationExpireDt: member.registration?.registrationExpireDt,
  };
}

/** Type inferred from the factory — import this wherever a view-model type is needed. */
export type RosterMemberView = ReturnType<typeof createMemberView>;

export function memberViewFromList(
  members: RosterMember[],
): RosterMemberView[] {
  return members.map(createMemberView);
}

/** The table's search: name, member ID, positions, programs, sub-units */
export function matchesSearch(
  member: RosterMemberView,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    member.preferredName,
    member.memberId,
    ...member.positions.map(p => p.position),
    ...member.programs.map(p => p || ''),
    ...member.subUnitNames.map(p => p || ''),
  ]
    .join('\0')
    .toLowerCase();
  return haystack.includes(q);
}
